import { Alert, Autocomplete, Button, Checkbox, CreateCustomerModal, CurrencyInput, DatePicker, FileUploader, Grid, Input, Layout, Loader, NumericInput, Typography } from "../../../components";
import { Paper } from "../../../components";
import { useFormik } from "formik";
import { UpdateOrderValidationSchema, requiresPassengerBirthDate } from "@next-pontos/validations";
import {useEffect, useMemo, Fragment, useContext, useRef} from 'react'
import { AlertTitle, Chip, FormControlLabel, IconButton, Link, Tooltip } from "@mui/material";
import { formatCNPJ, formatCPF, formatDate, formatDateTime, formatMoney, formatNumber, getBidStatusColor, getBidStatusLabel, getOrderConfirmationColor, getOrderConfirmationLabel, getPaymentStatusColor, getPaymentStatusLabel, momentFromCalendarDate, nl2br, shouldKeepCanceledCpfAsUsed, toCalendarDateString } from "../../../utils";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AIRLINES_OPTIONS, COMPANY_OPTIONS } from "../../../constants";
import currency from "currency.js";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAPI, useAxios, useToggle } from "../../../hooks";
import AuthContext from "../../../context/auth";
import { ContentCopy, Done, Download } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import * as Yup from 'yup'
import { BoardguardCard } from "./BoardguardCard";
import { NoShowRow } from "./NoShowRow";

export function Order () { 
    const navigate = useNavigate()
    const {me} = useContext(AuthContext)
    const params = useParams()
    
    const [customers, refetchCustomers] = useAxios(`/customer`)
    const [createCustomerModalVisibility, createCustomerModalVisibilityControls] = useToggle(false)
    const customersOptions = (customers?.data || []).map((customer: {name: string, id: string, is_business: boolean, document_number: string }) => ({
        label: `${customer.name} (${customer.is_business ? formatCNPJ(customer.document_number) : formatCPF(customer.document_number)})`,
        value: customer.id,
    }))

    const [coupons] = useAxios(`/coupon`)

    const [_, downloadFile] = useAxios({
        url: '/file',
        method: 'GET'
    }, {
        manual: true
    })

    const [order, refetchOrder] = useAxios(`/order/${params.id}`)
    const [orderUpdate, updateOrder] = useAxios({
        method: 'PUT',
        url: `/order/${params.id}`
    }, {
        manual: true
    })

    const [, createOrderComment] = useAxios({
        method: 'POST',
        url: `/order/${params.id}/comment`
    }, {
        manual: true
    })

    const bid = order?.data?.bid
    
    const [cards] = useAxios(`/card`)
    const validateHP = useAPI()

    const [password, getPassword] = useAxios(`/account/${bid?.account_id}/password`, {
        manual: true
    })

    const formik = useFormik({
        initialValues: {
            flight_date: '',
            airline: '',
            reservation_code: '',
            reservation_code_2: '',
            amount: '',
            fees: 0,
            additional_cost: 0,
            card_id: '',
            sell_price: 0,
            coupon_id: '',
            hp_id: '',
            pax: '',
            author_name: '',
            passengers: [{ name: '', surname: '', birth_date: '' }],
            origin_airport_code: '',
            description: '',
            customer_id: '',
            to_receive: false,
            income_due_date: null as string | null,
            created_at: '',
            company: '',
            file: null as unknown as File
        },
        initialStatus: 'draft',
        onSubmit: async (values) => {
            const payload = {
                ...values,
                income_due_date: values.to_receive ? values.income_due_date : null,
            }

            const formData = new FormData();
            
            const fields = Object.entries(payload)

            for (const field of fields) {
                const [key, value] = field
                let skip = false

                if (key === 'to_receive' || key === 'created_at' || key === 'company') {
                    skip = true
                }
                
                if (key === 'hp_id' && !value) {
                    skip = true
                }

                if (key === 'file') {
                    const file = value as File
                    
                    const firstPassenger = values.passengers?.[0]
                    const fallbackPaxName = firstPassenger ? `${firstPassenger.name} ${firstPassenger.surname}` : order.data.pax_name
                    if (file?.name === `Bilhete #${order.data.id} ${fallbackPaxName}` || !file) {
                        skip = true
                    }
                }

                if (key === 'passengers') {
                    formData.append(key, JSON.stringify(value))
                    continue
                }

                if (key === 'flight_date' || key === 'income_due_date') {
                    const dateStr = toCalendarDateString(value as string | Date)
                    if (dateStr) {
                        formData.append(key, dateStr)
                    }
                    continue
                }

                if (!skip && value !== null && value !== undefined) {
                    formData.append(key, value as string)
                }
            }

            const { status } = await updateOrder({
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            if (status <= 299) {
                enqueueSnackbar('Emissão atualizada com sucesso!', {variant: 'success'})
                window.location.reload()
            }
        },
        validateOnMount: true,
        validationSchema: UpdateOrderValidationSchema
    })

    const commentForm = useFormik({
        initialValues: {
            message: ''
        },
        validationSchema: Yup.object({
            message: Yup.string().required("Este campo é obrigatório")
        }),
        onSubmit: async (values, helpers) => {
            const response = await createOrderComment({
                url: `/order/${params.id}/comment`,
                data: {
                    comment: values.message,
                },
                method: 'POST'
            })

            if (response.status === 204) {
                enqueueSnackbar('Comentário enviado com sucesso', {variant: 'success'})
                refetchOrder()
                helpers.resetForm()
            }
        }
    })

    const chatContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (order?.data?.order_comments && chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current?.scrollHeight
        }
    }, [order?.data?.order_comments])

    useEffect(() => {
        if (order.data) {
            formik.resetForm({
                values: {
                    flight_date: toCalendarDateString(order.data.flight_date),
                    airline: order.data.airline,
                    reservation_code: order.data.reservation_code,
                    reservation_code_2: order.data.reservation_code_2 || '',
                    amount: order.data.miles,
                    fees: Number(order.data.fees),
                    additional_cost: Number(order.data.additional_cost),
                    card_id: order.data.card_id,
                    sell_price: currency(Number(order.data.sell_price)).add(order.data.coupon_discount || 0).value,
                    coupon_id: order.data.coupon_id,
                    pax: order.data.pax,
                    author_name: order.data.author_name,
                    passengers: (() => {
                        const rows = order.data.order_pax || []
                        if (rows.length > 0) {
                            return [{
                                name: rows[0].name,
                                surname: rows[0].surname,
                                birth_date: toCalendarDateString(rows[0].birth_date),
                            }]
                        }
                        const fullName = String(order.data.pax_name || '').trim()
                        if (!fullName) return [{ name: '', surname: '', birth_date: '' }]
                        const blocks = fullName.split(' ')
                        const name = blocks.shift() || ''
                        const surname = blocks.join(' ') || 'SEM SOBRENOME'
                        return [{ name, surname, birth_date: '' }]
                    })(),
                    origin_airport_code: order.data.origin_airport_code,
                    description: order.data.description,
                    hp_id: order.data.hp_id || '',
                    customer_id: order.data.customer_id,
                    to_receive: order.data.incomes.length > 0,
                    income_due_date: order.data.incomes.length > 0
                        ? toCalendarDateString(order.data.incomes[0].due_date)
                        : null,
                    created_at: order.data.created_at,
                    company: order.data.bid?.company || '',
                    file: order.data.order_ticket_file.length ? new File([""], `Bilhete #${order.data.id} ${order.data.pax_name || ''}`, {
                        type: "text/plain",
                        lastModified: Date.now(),
                      })
                    : null as unknown as File
                },
                touched: {},
                status: 'initiated'
            })
        }
    }, [order.data])
    
    const CARDS_OPTIONS = useMemo(() => {
        return (cards?.data || []).map((card: { id: number, last_four_digits: string, brand: string, alias: string}) => ({
            value: card.id,
            label: `**** ${card.last_four_digits} (${card.alias})`
        }))
    }, [cards])

    const totalCost = useMemo(() => {
        const amount_k = currency(formik.values.amount || 0, {precision: 3}).divide(1000).value
        const price_per_k = bid?.price_per_k || 0
        const fees = formik.values.fees || 0
        const additional_cost = formik.values.additional_cost || 0
        
        const miles_cost = currency(amount_k, {precision: 3}).multiply(price_per_k)
        const final_cost = miles_cost.add(fees).add(additional_cost)

        return final_cost.value
    }, [formik, bid])

    const airlineOptions = useMemo(() => {
        return AIRLINES_OPTIONS.map(item => ({
            value: item.label,
            label: item.label
        })).sort((a, b) => a.label.localeCompare(b.label))
    }, [])

    const paxOptions = useMemo(() => {
        const maxPax = bid?.pax || 9
        
        const maxPaxOptions = (new Array(maxPax).fill(0)).map((_, index) => {
            return {
                label: (index + 1).toString(),
                value: (index + 1).toString()
            }
        })

        if (!bid?.pax) {
            return maxPaxOptions
        } else {
            const usedPax = bid?.orders
            ?.filter((item: { id: number}) => item.id !== Number(params.id))
            ?.filter((item: {confirmation: string}) =>
                shouldKeepCanceledCpfAsUsed(bid?.company)
                    ? ['PENDING', 'CONFIRMED', 'CANCELLED'].includes(item.confirmation)
                    : ['PENDING', 'CONFIRMED'].includes(item.confirmation)
            )?.map((item: {pax: number}) => item.pax)?.reduce((prev: number, curr: number) => prev + curr, 0)
            
            return maxPaxOptions.filter((_, index) => index < (maxPaxOptions.length - usedPax))
        }

    }, [bid?.pax, bid?.orders])

    const milesAvailable = useMemo(() => {
        const usedMiles = bid?.orders
        ?.filter((item: { id: number}) => item.id !== Number(params.id))
        ?.filter((item: {confirmation: string}) => item.confirmation === 'PENDING' || item.confirmation === 'CONFIRMED')?.map((item: {miles: number}) => item.miles)?.reduce((prev: number, curr: number) => prev + curr, 0)
    
        return currency(bid?.amount).subtract(usedMiles).value
    }, [bid])

    const { amount, additional_cost, fees, sell_price } = formik.values
    const { setErrors }  = formik

    useEffect(() => {
        if (Number(amount) > Number(milesAvailable)) {
            setErrors({ amount: 'A quantidade de milhas deve ser menor que o disponível na oferta' })
        }
    }, [amount, milesAvailable, setErrors])
    
    const disabledForm = useMemo(() => {
        // REMOVER
        return false
        return order?.data?.bid?.status === 'DONE' || order?.data?.payment?.status === 'PAID'
    }, [order.data])

    const soldCostPerK = useMemo(() => {
        const totalMilesCost = currency(sell_price).subtract(fees).subtract(additional_cost).value
        return currency(totalMilesCost).divide(currency(amount, {precision: 3}).divide(1000)).value
    }, [additional_cost, amount, fees, sell_price])
    

    const couponsOptions: Array<{label: string, value: number | string }> = useMemo(() => {
        return (coupons.data || []).map((item: {code: string, id: number, discount_method: 'MONEY' | 'PERCENTAGE', discount: number}) => ({
            label: `${item.code} (${item.discount_method === 'MONEY' ? formatMoney(Number(item.discount)) : `${formatNumber(Number(item.discount))}%`})`,
            value: item.id
        }))
    }, [coupons])

    const couponDiscount = useMemo(() => {
        const coupon = (coupons.data || []).find((item: {id: string}) => item.id === formik.values.coupon_id)

        if (!coupon) {
            return 0
        }

        const discount = coupon?.discount_method === 'MONEY' ? coupon?.discount : currency(formik.values.sell_price).multiply(1 - currency(coupon?.discount).divide(100).value).value

        return discount || 0
    }, [formik.values.coupon_id, coupons, formik.values.sell_price])

    return (
        <Layout>
            {createCustomerModalVisibility && (<CreateCustomerModal 
                open={createCustomerModalVisibility}
                onClose={createCustomerModalVisibilityControls.setFalse}
                onSubmit={async (customerId) => {
                    await refetchCustomers()

                    formik.setFieldValue('customer_id', customerId)
                }}
            />)}
            <Grid container justifyContent="center" gap={4} minHeight={"calc(100vh - 64px)"} alignItems="flex-start" paddingTop={5}>
                {order.loading || orderUpdate.loading || cards.loading || formik.status === 'draft' ? (
                    <div style={{
                        display: 'flex',
                        flex: '1',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Loader />
                    </div>
                ) : (<Fragment>
                <Grid item xs={12} sm={4}>
                    <Alert color="warning" icon={false}>
                        <strong>Atenção!</strong>
                        <br />
                        Alterar os dados da emissão pode interferir no valor do pagamento ao fornecedor.
                    </Alert>
                    {disabledForm && (<Alert color="error" icon={false} sx={{
                        marginTop: 2
                    }}>
                        <strong>Atenção!</strong>
                        <br />
                        Não é possível alterar emissões de ofertas finalizadas e emissões que já foram pagas. Se você deseja alterar confirmar ou cancelar altere pela <Link href="/admin/emissoes">lista de emissões.</Link>
                    </Alert>)}
                    <Paper elevation={1} sx={{
                        marginTop: 4
                    }}>
                    <form onSubmit={formik.handleSubmit}>
                    <Grid container padding={2} spacing={2} justifyContent="center">
                        <Grid item xs={12} marginBottom={2}>
                            <Typography textAlign={"center"} variant="h5">Editar Emissão</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography fontWeight="bold">
                                        Data de Emissão:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography textAlign="end">
                                        {formatDateTime(new Date(formik.values.created_at))}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete 
                                label="Cliente"
                                name="customer_id"
                                onChange={({value}) => {
                                    formik.setFieldValue('customer_id', value, true)
                                }}
                                options={customersOptions}
                                onBlur={formik.handleBlur}
                                value={customersOptions.find((item: { value: string }) => item.value === formik.values.customer_id) || null}
                                helperText={(formik.touched.customer_id || formik.submitCount > 0) && formik.errors.customer_id}
                                error={!!(formik.touched.customer_id || formik.submitCount > 0) && !!formik.errors.customer_id}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <DatePicker 
                                label="Data do Voo"
                                value={momentFromCalendarDate(formik.values.flight_date)}
                                onChange={(value) => {
                                    formik.setFieldValue(
                                        'flight_date',
                                        value?.isValid() ? value.format('YYYY-MM-DD') : '',
                                        true
                                    )
                                }}
                                disabled={disabledForm}
                                helperText={(formik.touched.flight_date || formik.submitCount > 0) && formik.errors.flight_date}
                                error={!!(formik.touched.flight_date || formik.submitCount > 0) && !!formik.errors.flight_date}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}> 
                            <Autocomplete 
                                label="Companhia"
                                onChange={({value}) => {
                                    formik.setFieldValue('airline', value, true)
                                }}
                                options={airlineOptions}
                                onBlur={formik.handleBlur}
                                value={airlineOptions.find(item => item.value === formik.values.airline)}
                                helperText={formik.touched.airline && formik.errors.airline}
                                error={!!formik.touched.airline && !!formik.errors.airline}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Input 
                                label="Emissor"
                                name="author_name"
                                onChange={formik.handleChange}
                                value={formik.values.author_name}
                                helperText={formik.touched.author_name && formik.errors.author_name}
                                error={!!formik.touched.author_name && !!formik.errors.author_name}
                                onBlur={formik.handleBlur}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Input 
                                label="Localizador 1"
                                name="reservation_code"
                                onChange={event => {
                                    formik.setFieldValue('reservation_code' ,event.target.value.toUpperCase().trim())
                                }}
                                value={formik.values.reservation_code}
                                helperText={formik.touched.reservation_code && formik.errors.reservation_code}
                                error={!!formik.touched.reservation_code && !!formik.errors.reservation_code}
                                onBlur={formik.handleBlur}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Input 
                                label="Localizador 2 (opcional)"
                                name="reservation_code_2"
                                onChange={event => {
                                    formik.setFieldValue('reservation_code_2' ,event.target.value.toUpperCase().trim())
                                }}
                                value={formik.values.reservation_code_2}
                                helperText={formik.touched.reservation_code_2 && formik.errors.reservation_code_2}
                                error={!!formik.touched.reservation_code_2 && !!formik.errors.reservation_code_2}
                                onBlur={formik.handleBlur}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <NumericInput
                                label="Passageiros na reserva (CPFs)"
                                name="pax"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.pax === '' || formik.values.pax === null || formik.values.pax === undefined ? '' : String(formik.values.pax)}
                                helperText={formik.touched.pax && formik.errors.pax}
                                error={!!formik.touched.pax && !!formik.errors.pax}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Input
                                label="Nome do PAX"
                                value={formik.values.passengers?.[0]?.name ?? ''}
                                onChange={(e) => {
                                    const current = formik.values.passengers?.[0] ?? { name: '', surname: '', birth_date: '' }
                                    formik.setFieldValue('passengers', [{ ...current, name: e.target.value }], true)
                                }}
                                onBlur={() => {
                                    formik.setFieldTouched('passengers', true)
                                }}
                                helperText={
                                    (formik.touched.passengers || formik.submitCount > 0) &&
                                    typeof formik.errors.passengers === 'string'
                                        ? formik.errors.passengers
                                        : undefined
                                }
                                error={
                                    !!(formik.touched.passengers || formik.submitCount > 0) &&
                                    !!formik.errors.passengers
                                }
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Input
                                label="Sobrenome do PAX"
                                value={formik.values.passengers?.[0]?.surname ?? ''}
                                onChange={(e) => {
                                    const current = formik.values.passengers?.[0] ?? { name: '', surname: '', birth_date: '' }
                                    formik.setFieldValue('passengers', [{ ...current, surname: e.target.value }], true)
                                }}
                                onBlur={() => {
                                    formik.setFieldTouched('passengers', true)
                                }}
                                error={
                                    !!(formik.touched.passengers || formik.submitCount > 0) &&
                                    !!formik.errors.passengers
                                }
                                disabled={disabledForm}
                            />
                        </Grid>
                        {requiresPassengerBirthDate(formik.values.company || bid?.company) && (
                            <Grid item xs={12} sm={4}>
                                <DatePicker
                                    label="Data de Nascimento do PAX"
                                    value={momentFromCalendarDate(formik.values.passengers?.[0]?.birth_date)}
                                    onChange={(value) => {
                                        const current = formik.values.passengers?.[0] ?? { name: '', surname: '', birth_date: '' }
                                        formik.setFieldValue(
                                            'passengers',
                                            [{
                                                ...current,
                                                birth_date: value?.isValid() ? value.format('YYYY-MM-DD') : '',
                                            }],
                                            true
                                        )
                                        formik.setFieldTouched('passengers', true)
                                    }}
                                    helperText={
                                        (formik.touched.passengers || formik.submitCount > 0) &&
                                        typeof formik.errors.passengers === 'string'
                                            ? formik.errors.passengers
                                            : undefined
                                    }
                                    error={
                                        !!(formik.touched.passengers || formik.submitCount > 0) &&
                                        !!formik.errors.passengers
                                    }
                                    disabled={disabledForm}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={4}>
                            <NumericInput
                                label="Milhas"
                                name="amount"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.amount}
                                helperText={formik.touched.amount && formik.errors.amount}
                                error={!!formik.touched.amount && !!formik.errors.amount}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CurrencyInput
                                label="Taxas (R$)"
                                name="fees"
                                onChangeValue={(_, originalValue) => {
                                    formik.setFieldValue('fees', originalValue, true)
                                }}
                                onBlur={formik.handleBlur}
                                value={formik.values.fees}
                                helperText={formik.touched.fees && formik.errors.fees}
                                error={!!formik.touched.fees && !!formik.errors.fees}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Tooltip title="Bagagem despachada, marcação de assentos, etc..." >
                                <div>
                                <CurrencyInput
                                    label="Custos Adicionais (R$)"
                                    name="additional_cost"
                                    onChangeValue={(_, originalValue) => {
                                        formik.setFieldValue('additional_cost', originalValue, true)
                                    }}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.additional_cost}
                                    helperText={formik.touched.additional_cost && formik.errors.additional_cost}
                                    error={!!formik.touched.additional_cost && !!formik.errors.additional_cost}
                                    disabled={disabledForm}
                                />
                                </div>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CurrencyInput
                                label="Valor de Venda (R$)"
                                name="sell_price"
                                onChangeValue={(_, originalValue) => {
                                    formik.setFieldValue('sell_price', originalValue, true)
                                }}
                                onBlur={formik.handleBlur}
                                value={formik.values.sell_price}
                                helperText={formik.touched.sell_price && formik.errors.sell_price}
                                error={!!formik.touched.sell_price && !!formik.errors.sell_price}
                                disabled={disabledForm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Tooltip title="Cartão utilizado para pagamento das taxas." placement="top">
                                <div>
                                <Autocomplete 
                                    label="Cartão"
                                    onChange={({value}) => {
                                        formik.setFieldValue('card_id', value, true)
                                    }}
                                    options={CARDS_OPTIONS}
                                    onBlur={formik.handleBlur}
                                    value={CARDS_OPTIONS.find((item: { value: string }) => item.value === formik.values.card_id) || null}
                                    helperText={formik.touched.card_id && formik.errors.card_id}
                                    error={!!formik.touched.card_id && !!formik.errors.card_id}
                                    disabled={disabledForm}
                                />
                            </div>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Input
                                label="Aeroporto de Origem"
                                name="origin_airport_code"
                                onChange={e => {
                                    formik.setFieldValue('origin_airport_code', e.target.value.replace(/([^A-Z])/ig, '').toLocaleUpperCase().slice(0, 3), true)
                                }}
                                onBlur={formik.handleBlur}
                                value={formik.values.origin_airport_code}
                                helperText={formik.touched.origin_airport_code && formik.errors.origin_airport_code}
                                error={!!formik.touched.origin_airport_code && !!formik.errors.origin_airport_code}
                            />
                        </Grid>
                            <Grid item xs={4}>
                                <Input 
                                    label="Observação"
                                    name="description"
                                    onChange={formik.handleChange}
                                    value={formik.values.description}
                                    helperText={formik.touched.description && formik.errors.description}
                                    error={!!formik.touched.description && !!formik.errors.description}
                                    onBlur={formik.handleBlur}
                                    multiline
                                />
                            </Grid>
                              <Grid item xs={4}> 
                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            checked={formik.values.to_receive}
                                            onChange={event => {
                                                formik.setFieldValue('to_receive', event.target.checked);
                                            }}
                                            name="to_receive" 
                                        />
                                    } 
                                    label="Á receber" 
                                />
                            </Grid>
    
                            <Grid item xs={12} sm={4}>
                                {formik.values.to_receive && (
                                    <DatePicker 
                                        label="Data recebimento"
                                        value={momentFromCalendarDate(formik.values.income_due_date)}
                                        onChange={(value) => {
                                            formik.setFieldValue(
                                                'income_due_date',
                                                value?.isValid() ? value.format('YYYY-MM-DD') : null,
                                                true
                                            )
                                        }}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}> 
                                <Grid container>
                                    {order.data.order_ticket_file.length ? (
                                        <Grid item xs={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                                            <IconButton
                                                name="download"
                                                onClick={() => {
                                                    const file_id = order.data.order_ticket_file[0].id
                                                    
                                                    downloadFile({
                                                        url: `/file/${file_id}`,
                                                        responseType: 'blob'
                                                    }).then((res) => {
                                                        if (res.status === 200) {
                                                            const blobUrl = URL.createObjectURL(res.data);

                                                            window.open(blobUrl, '_blank')
                                                        }
                                                    })
                                                }}
                                            >
                                                <Download />
                                            </IconButton>
                                        </Grid>
                                    ) : null}
                                    <Grid item xs={11}>
                                        <FileUploader 
                                            label="Bilhete"
                                            onChange={(value) => {
                                                formik.setFieldValue('file', value, true)
                                            }}
                                            value={formik.values.file}
                                            inputProps={{
                                                accept: 'image/*, .pdf'
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={10}>
                                <Input 
                                    label="HP"
                                    name="hp_id"
                                    onChange={formik.handleChange}
                                    value={formik.values.hp_id}
                                    helperText={formik.touched.hp_id && formik.errors.hp_id}
                                    error={!!formik.touched.hp_id && !!formik.errors.hp_id}
                                    onBlur={formik.handleBlur}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <Button 
                                style={{
                                    height: '100%'
                                }}
                                fullWidth
                                    onClick={async () => {
                                        const {status} = await validateHP.post('/order/validate-hp', {
                                            hp_id: formik.values.hp_id,
                                            customer_id: formik.values.customer_id
                                        })

                                        if (status < 299) {
                                            enqueueSnackbar('HP válido!', {variant: 'success'})
                                        }
                                    }}
                                    disabled={!formik.values.customer_id || formik.values.hp_id.length !== 12 || !formik.values.hp_id.toLowerCase().startsWith('hp')}
                                >
                                    <Done />
                                </Button>
                            </Grid>
                            {/* <Grid item xs={12}>
                                <Autocomplete 
                                    label="Cupom"
                                    onChange={({value}) => formik.setFieldValue('coupon_id', value, true)}
                                    options={couponsOptions}
                                    onBlur={formik.handleBlur}
                                    value={couponsOptions.find(item => item.value === formik.values.coupon_id)}
                                    helperText={formik.touched.coupon_id && formik.errors.coupon_id}
                                    error={!!formik.touched.coupon_id && !!formik.errors.coupon_id}
                                />
                            </Grid> */}
                            <Grid item xs={12}>
                                <Grid container marginTop={2}>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Status:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} textAlign="right">
                                        <Chip color={getOrderConfirmationColor({
                                            confirmation: order?.data?.confirmation,
                                            created_at: order?.data?.created_at
                                        })} label={getOrderConfirmationLabel(order?.data?.confirmation)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            {order?.data?.id && (
                                <NoShowRow
                                    orderId={order.data.id}
                                    fields={order.data}
                                    ticket={order.data.boardguard_ticket}
                                />
                            )}
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography fontWeight="bold">
                                        Custo Total:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography textAlign="end">
                                        {formatMoney(totalCost)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography fontWeight="bold">
                                        Descontos:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography textAlign="end">
                                        {formatMoney(couponDiscount)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography fontWeight="bold">
                                        RAM:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography textAlign="end">
                                        {formatMoney(currency(formik.values.sell_price).subtract(totalCost).value)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        {me?.role === 'ADMIN' && (<Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Typography fontWeight="bold">
                                        Milheiro Vendido:
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography textAlign="end">
                                        {
                                            formatMoney(soldCostPerK)
                                        }
                                        {` (${currency(currency(soldCostPerK).subtract(bid?.price_per_k || 0)).divide(bid?.price_per_k || 0).value} %)`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>)}
                        <Grid item marginTop={2}>
                            <Button disabled={!formik.isValid || disabledForm} type="submit">Editar</Button>
                        </Grid>
                    </Grid>
                    </form>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper elevation={1} >
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={12} marginBottom={2}>
                                <Typography textAlign={"center"} variant="h5">Detalhes da Oferta</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            ID:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} textAlign="end">
                                    <Link href={`/admin/ofertas/${bid?.id}`}>
                                        # {bid?.id}
                                    </Link>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={3}>
                                        <Typography fontWeight="bold">
                                            Data:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography textAlign="end">
                                            {moment(bid?.created_at).format('DD/MM/YYYY HH:mm:ss')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={3}>
                                        <Typography fontWeight="bold">
                                            Status:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9} textAlign="end">
                                        <Chip color={getBidStatusColor(bid.status)} label={getBidStatusLabel(bid.status)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Fornecedor:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.user?.name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={3}>
                                        <Typography fontWeight="bold">
                                            Companhia:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography textAlign="end">
                                            {COMPANY_OPTIONS.find(item => item.value === bid?.company)?.label}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={3}>
                                        <Typography fontWeight="bold">
                                            Quantidade:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9} display="flex" flexDirection="row" justifyContent="flex-end">
                                        <Typography textAlign="end">
                                            {Number(milesAvailable).toLocaleString('pt-BR')}
                                        </Typography>
                                        {milesAvailable < Number (bid?.amount) && (<Typography textAlign="end" marginLeft={1} color="red" fontWeight="bold">
                                            (-{Number(currency(bid?.amount).subtract(milesAvailable).value).toLocaleString('pt-BR')})
                                        </Typography>)}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={4}>
                                        <Typography fontWeight="bold">
                                            CPFs Disponíveis:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8} display="flex" flexDirection="row" justifyContent="flex-end">
                                        <Typography textAlign="end">
                                            {bid?.pax === null ? '∞' : Number(paxOptions[paxOptions.length - 1]?.value || 0)}
                                        </Typography>
                                        {bid?.pax !== null && Number(paxOptions[paxOptions.length - 1]?.value || 0) < bid?.pax && (
                                            <Typography textAlign="end" marginLeft={1} color="red" fontWeight="bold">
                                                (-{currency(bid?.pax).subtract(Number(paxOptions[paxOptions.length - 1]?.value || 0))?.value})
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={4}>
                                        <Typography fontWeight="bold">
                                            Custo do Milheiro:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography textAlign="end">
                                            {formatMoney(bid?.price_per_k)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={4}>
                                        <Typography fontWeight="bold">
                                            Emissões da Oferta:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8} textAlign="end">
                                        {
                                            bid?.orders ? bid?.orders?.map((item: {id: string}) => {
                                                return (
                                                    <Fragment>
                                                    &nbsp;
                                                    <Link href={`/admin/emissoes/${item.id}`}>
                                                        # {item.id}
                                                    </Link>
                                                    </Fragment>
                                                )
                                            }) : (
                                                <Typography>
                                                    -
                                                </Typography>
                                            )
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>     
                            {order?.data?.bid?.status === 'ON_ORDER' && (
                                <Grid item xs={12} textAlign="center">
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        navigate(`/admin/ofertas/${order?.data?.bid_id}/emissao`)
                                    }}
                                >
                                    Emitir novamente nesta oferta
                                </Button>
                            </Grid>)}
                        </Grid>
                    </Paper>
                    <Paper elevation={1} style={{ marginTop: 30 }}>
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={12} marginBottom={2}>
                                <Typography textAlign={"center"} variant="h5">Dados da Conta</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction={{ xs: 'column', sm: 'row' }}>
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Login:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.account?.login}
                                            <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                navigator?.clipboard?.writeText(bid?.account?.login)
                                                enqueueSnackbar('Copiado!', {variant: 'success'})
                                            }}>
                                                <ContentCopy />
                                            </IconButton>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Senha:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                            <Typography textAlign="end">
                                        {
                                            password?.data ? (
                                                <Fragment>
                                                    {password?.data?.password}
                                                    <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => {
                                                        navigator?.clipboard?.writeText(password?.data?.password)
                                                        enqueueSnackbar('Copiado!', {variant: 'success'})
                                                    }}>
                                                        <ContentCopy />
                                                    </IconButton>
                                                </Fragment>
                                            ) : (
                                            <div style={{cursor: 'pointer'}} onClick={() => getPassword()}>
                                                <VisibilityIcon fontSize="inherit" />
                                            </div>
                                            )
                                        }
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Nome do Titular:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.account?.holder_name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            E-mail do Titular:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {bid?.account?.holder_email}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            CPF do Titular:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {formatCPF(bid?.account?.holder_cpf || '')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Data de Nasc. do Titular:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {formatDate(bid?.account?.holder_birthday || '')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                    <Grid container direction="row">
                      <Grid item xs={6}>
                        <Typography fontWeight="bold">
                          Número de membro:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography textAlign="end">
                          {bid?.account?.membership_number}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                        </Grid>
                    </Paper>
                    <Paper elevation={1} style={{ marginTop: 30 }}>
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={12} marginBottom={2}>
                                <Typography textAlign={"center"} variant="h5">Dados do Pagamento</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            ID:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} textAlign="end">
                                        <Link href={`/admin/pagamentos/${order?.data?.payment?.id}`}>
                                            # {order?.data?.payment?.id}
                                        </Link>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Vencimento:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {formatDate(order?.data?.payment?.due_date)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Status:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} textAlign="end">
                                        <Chip color={getPaymentStatusColor(order?.data?.payment?.status)} label={getPaymentStatusLabel(order?.data?.payment?.status)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row">
                                    <Grid item xs={6}>
                                        <Typography fontWeight="bold">
                                            Valor:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography textAlign="end">
                                            {formatMoney(order?.data?.payment?.amount)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                    {order?.data?.id && (
                        <BoardguardCard
                            orderId={order.data.id}
                            reservationCode={order.data.reservation_code}
                            ticket={order.data.boardguard_ticket}
                            supported={order.data.boardguard_supported}
                        />
                    )}
                    <Paper elevation={1} style={{ marginTop: 30 }}>
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={12} marginBottom={2}>
                                <Typography textAlign={"center"} variant="h5">Comentários</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <div
                                    ref={chatContainerRef}
                                    style={{
                                        maxHeight: 300,
                                        overflowY: 'scroll',
                                        marginBottom: 15
                                    }}
                                >
                                    {
                                        (order.data?.order_comments || []).map((item: { id: number; created_at: string; comment: string; user: { id: number; name: string } }, index: number) => {
                                            return (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        justifyContent: me?.id !== item.user.id ? 'left' : 'right'
                                                    }}
                                                >
                                                    <Alert icon={false} style={{
                                                        width: '80%',
                                                        marginTop: index > 0 ? 10 : undefined
                                                    }} color="info">
                                                        <AlertTitle>
                                                            {formatDateTime(new Date(item.created_at))} - {item.user.name}
                                                        </AlertTitle>
                                                        <p dangerouslySetInnerHTML={{
                                                            __html: nl2br(item.comment)
                                                        }} />
                                                    </Alert>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                                <div style={{display: 'flex', flexDirection: 'row', gap: 5}}>
                                    <Input
                                        name="message"
                                        label="Comentário"
                                        multiline
                                        maxRows={4}
                                        rows={4}
                                        disabled={commentForm.isSubmitting}
                                        value={commentForm.values.message}
                                        onChange={commentForm.handleChange}
                                        onBlur={commentForm.handleBlur}
                                        helperText={commentForm.touched.message && commentForm.errors.message}
                                        error={!!commentForm.touched.message && !!commentForm.errors.message}
                                    />

                                    <Button
                                        disabled={commentForm.isSubmitting || !commentForm.values.message}
                                        onClick={commentForm.submitForm}
                                    >
                                        Enviar
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                </Fragment>)}
            </Grid>
        </Layout>
    )
}